import {
  FlowStep,
  FundingOption,
  FundingOriginInput,
  UpsertFlowStepInput,
} from '@varo/graphql-schema-types';
import { useFocusEffect, useRouter } from 'expo-router';
import { navigateToDirectDepositSetup } from 'features/directDeposit/navigation/navigateToDirectDepositSetup';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import FundingSelectionScreen from 'features/onboarding/components/FundingSelectionScreen/FundingSelectionScreen';
import useFundingNavigation from 'features/onboarding/hooks/useFundingNavigation';
import useGetFlowStepId from 'features/onboarding/hooks/useGetFlowStepId';
import useGetFundingOptions from 'features/onboarding/hooks/useGetFundingOptions';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useDirectDepositGateway } from 'shared/hooks/pinwheel/useDirectDepositGateway/useDirectDepositGateway';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import {
  ErrorScreen,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';

const FundingScreen = () => {
  const router = useRouter();
  const externalNavigateRef = useRef(false);
  const upsertInputRef = useRef({});
  const [shouldRenderDirectDepositIntro, setShouldRenderDirectDepositIntro] =
    useState(true);

  const { fundingOptionsData, error, fetching } = useGetFundingOptions({
    origin: FundingOriginInput.Onboarding,
  });

  const { defaultMessage, graphqlErrorsTitle } = useDefaultErrorStrings();
  const { t: translatedText } = useTranslation('onboarding');

  const flowStepId = useGetFlowStepId();

  const { navigateFundingAction, pinWheelLoading, directDepositIntroProps } =
    useFundingNavigation();

  const { isUpsertFlowStepLoading, submitUpsertFlowStep } = useUpsertFlowStep();
  const { directDepositGatewayFlag } = useDirectDepositGateway();
  useViewDidFocusScreenAnalyticsEvent({
    key: 'funding',
  });

  const handleContinueButton = async (
    id: string | undefined,
    didSkip?: boolean,
  ) => {
    const isSkipped = Boolean(didSkip);
    if (id === FundingOption.DirectDeposit) {
      externalNavigateRef.current = false;
      setShouldRenderDirectDepositIntro(true);
    }
    const input: UpsertFlowStepInput = {
      id: flowStepId,
      intentBasedOnboarding: {
        selection: undefined,
        didSkip: isSkipped,
      },
    };

    const submitHandler = async () => {
      try {
        const result = await submitUpsertFlowStep({ input });
        if (!result.error) {
          const flowStep = result.data?.upsertFlowStep as FlowStep;
          redirectUserToTargetRoute(flowStep);
        }
      } catch (error) {
        if (error instanceof AbortError) {
          return;
        }
        throw error;
      }
    };

    try {
      if (isSkipped) {
        await submitHandler();
      } else {
        const action = id as FundingOption;
        if (action === FundingOption.DirectDeposit) {
          externalNavigateRef.current = false;
        }
        upsertInputRef.current = input;

        navigateFundingAction(action, async (outcome) => {
          if (outcome?.success) {
            externalNavigateRef.current = true;
            await submitHandler();
          }
        });
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }
  };

  const navigateHandler = useCallback(async () => {
    if (externalNavigateRef.current) {
      externalNavigateRef.current = false;
      const res = await submitUpsertFlowStep({ input: upsertInputRef.current });
      if (!res.error) {
        redirectUserToTargetRoute(res.data?.upsertFlowStep as FlowStep);
      }
    }
  }, [submitUpsertFlowStep]);

  useFocusEffect(
    useCallback(() => {
      void navigateHandler();
    }, [navigateHandler]),
  );

  useEffect(() => {
    const shouldShowIntroScreen =
      directDepositGatewayFlag &&
      !!directDepositIntroProps &&
      shouldRenderDirectDepositIntro;

    if (!shouldShowIntroScreen || !directDepositIntroProps) {
      return;
    }

    setShouldRenderDirectDepositIntro(false);
    navigateToDirectDepositSetup({
      entry: 'onboarding-funding',
      flowId: flowStepId,
      sourceName: directDepositIntroProps.sourceName ?? 'onboarding_funding',
      abCohort: directDepositIntroProps.abCohort ?? '',
      isCoinLoader: false,
    });
  }, [
    directDepositGatewayFlag,
    directDepositIntroProps,
    shouldRenderDirectDepositIntro,
    fetching,
    isUpsertFlowStepLoading,
    pinWheelLoading,
    flowStepId,
  ]);

  return (
    <>
      {fundingOptionsData && (
        <FundingSelectionScreen
          optionsData={fundingOptionsData?.options}
          headlineText={fundingOptionsData?.title}
          onContinueButton={handleContinueButton}
          skipButton={
            fundingOptionsData?.skipButton
              ? fundingOptionsData.skipButton
              : undefined
          }
        />
      )}
      <ScreenTransitionOverlay
        isLoading={
          fetching ||
          isUpsertFlowStepLoading ||
          pinWheelLoading ||
          externalNavigateRef.current
        }
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={fetching || isUpsertFlowStepLoading}
            isPinwheelLoading={pinWheelLoading}
            defaultLoadingComponent={
              <FullScreenLoading
                isCoinLoader
                titleText={translatedText('loading.titleText')}
              />
            }
          />
        }
        ErrorComponent={
          error && error !== undefined ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={graphqlErrorsTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: translatedText('errors.goToHomePageButtonLabel'),
                onPress: () => {
                  router.push('/');
                },
              }}
            />
          ) : null
        }
      />
    </>
  );
};

export default FundingScreen;
